import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Upload as UploadIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { completeOnboarding, getExchanges, submitExchangeUID, uploadScreenshot } from '@/services/api';
import { PageShell } from '@/components/common/Page';
import LanguageSelector from '@/components/common/LanguageSelector';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import ExchangeLogo from '@/components/common/ExchangeLogo';
import type { TranslationKey } from '@/i18n/catalog';
import type { Exchange, UserExchange } from '@/types';

interface OnboardingPageProps {
  mockMode?: boolean;
}

const FAQ_ITEMS: Array<{ question: TranslationKey; answer: TranslationKey }> = [
  {
    question: 'onboarding.faqArbitrageQuestion',
    answer: 'onboarding.faqArbitrageAnswer',
  },
  {
    question: 'onboarding.faqStartQuestion',
    answer: 'onboarding.faqStartAnswer',
  },
  {
    question: 'onboarding.faqAccessQuestion',
    answer: 'onboarding.faqAccessAnswer',
  },
  {
    question: 'onboarding.faqRoundsQuestion',
    answer: 'onboarding.faqRoundsAnswer',
  },
];

const STEP_KEYS: TranslationKey[] = [
  'onboarding.stepFaq',
  'onboarding.stepExchanges',
  'onboarding.stepVerification',
];

const MOCK_EXCHANGES: Exchange[] = [
  {
    id: 'binance',
    name: 'Binance',
    logo_url: null,
    is_active: true,
    display_order: 1,
  },
  {
    id: 'gate',
    name: 'Gate',
    logo_url: null,
    is_active: true,
    display_order: 2,
  },
  {
    id: 'mexc',
    name: 'MEXC',
    logo_url: null,
    is_active: true,
    display_order: 3,
  },
  {
    id: 'htx',
    name: 'HTX',
    logo_url: null,
    is_active: true,
    display_order: 4,
  },
  {
    id: 'bingx',
    name: 'BingX',
    logo_url: null,
    is_active: true,
    display_order: 5,
  },
  {
    id: 'whitebit',
    name: 'WhiteBIT',
    logo_url: null,
    is_active: true,
    display_order: 6,
  },
  {
    id: 'bitget',
    name: 'Bitget',
    logo_url: null,
    is_active: true,
    display_order: 7,
  },
  {
    id: 'bybit',
    name: 'Bybit',
    logo_url: null,
    is_active: true,
    display_order: 8,
  },
  {
    id: 'okx',
    name: 'OKX',
    logo_url: null,
    is_active: true,
    display_order: 9,
  },
];

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

function OnboardingScrollFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        height: 'var(--app-viewport-stable-height, 100dvh)',
        minHeight: 'var(--app-viewport-stable-height, 100dvh)',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>{children}</Box>
    </Box>
  );
}

export default function OnboardingPage({ mockMode = false }: OnboardingPageProps) {
  const navigate = useNavigate();
  const { refetch: refetchAuth } = useAuth();
  const { t } = useI18n();
  const steps = STEP_KEYS.map((key) => t(key));

  const [activeStep, setActiveStep] = useState(0);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([]);
  const [exchangeUIDs, setExchangeUIDs] = useState<Record<string, string>>({});
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);
  const [submittedExchanges, setSubmittedExchanges] = useState<UserExchange[]>([]);
  const [mockCompleted, setMockCompleted] = useState(false);

  const { data: exchangesData, isLoading: exchangesLoading } = useQuery({
    queryKey: ['exchanges'],
    queryFn: getExchanges,
    enabled: !mockMode,
  });
  const exchanges = mockMode ? MOCK_EXCHANGES : exchangesData?.exchanges ?? [];

  const submitUIDMutation = useMutation({
    mutationFn: async ({ exchange_id, exchange_uid }: { exchange_id: string; exchange_uid: string }) => {
      if (!mockMode) {
        return submitExchangeUID(exchange_id, exchange_uid);
      }

      await wait(250);

      return {
        verification: {
          id: Date.now(),
          user_id: 0,
          exchange_id,
          exchange_uid,
          screenshot_url: null,
          verification_status: 'pending',
          rejection_reason: null,
          verified_at: null,
          created_at: new Date().toISOString(),
        } satisfies UserExchange,
      };
    },
    onSuccess: (data) => {
      setSubmittedExchanges((current) => [...current, data.verification]);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ verificationId, file }: { verificationId: number; file: File }) => {
      if (!mockMode) {
        return uploadScreenshot(verificationId, file);
      }

      await wait(300);

      return {
        success: true,
        screenshot_url: `mock-screenshot-${verificationId}-${file.name}`,
      };
    },
    onSuccess: (data, variables) => {
      setUploadingFor(null);
      setSubmittedExchanges((current) =>
        current.map((exchange) =>
          exchange.id === variables.verificationId
            ? { ...exchange, screenshot_url: data.screenshot_url }
            : exchange,
        ),
      );
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!mockMode) {
        return completeOnboarding();
      }

      await wait(250);
      return { success: true };
    },
    onSuccess: () => {
      if (mockMode) {
        setMockCompleted(true);
      } else {
        refetchAuth();
        navigate('/');
      }
    },
  });

  const handleRestartMock = () => {
    setActiveStep(0);
    setSelectedExchanges([]);
    setExchangeUIDs({});
    setUploadingFor(null);
    setSubmittedExchanges([]);
    setMockCompleted(false);
  };

  const handleExchangeToggle = (exchangeId: string) => {
    setSelectedExchanges((current) =>
      current.includes(exchangeId)
        ? current.filter((id) => id !== exchangeId)
        : [...current, exchangeId],
    );
  };

  const handleSubmitUID = async (exchangeId: string) => {
    const uid = exchangeUIDs[exchangeId];
    if (!uid) return;

    await submitUIDMutation.mutateAsync({ exchange_id: exchangeId, exchange_uid: uid });
  };

  const handleFileUpload = async (verificationId: number, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingFor(verificationId);
    await uploadMutation.mutateAsync({ verificationId, file });
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      completeMutation.mutate();
    } else {
      setActiveStep((current) => current + 1);
    }
  };

  const canProceed = () => {
    if (activeStep === 1) return selectedExchanges.length >= 1;
    if (activeStep === 2) return submittedExchanges.length >= 1;
    return true;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {FAQ_ITEMS.map((item, index) => (
              <Accordion key={item.question} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={800}>{t(item.question)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{t(item.answer)}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.selectExchanges')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('onboarding.selectExchangesHint')}
            </Typography>
            {exchangesLoading && !mockMode ? (
              <CircularProgress />
            ) : (
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {exchanges.map((exchange: Exchange) => (
                  <ListItem
                    key={exchange.id}
                    component="div"
                    onClick={() => handleExchangeToggle(exchange.id)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      bgcolor: selectedExchanges.includes(exchange.id)
                        ? 'rgba(118, 244, 184, 0.14)'
                        : 'rgba(244, 255, 249, 0.045)',
                      border: '1px solid rgba(226, 255, 238, 0.12)',
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedExchanges.includes(exchange.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={exchange.name} />
                    <Box sx={{ ml: 1 }}>
                      <ExchangeLogo src={exchange.logo_url} name={exchange.name} id={exchange.id} size={30} />
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );

      case 2:
        return (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('onboarding.verifyAccounts')}
              </Typography>
              <Typography color="text.secondary">
                {t('onboarding.verifyAccountsHint')}
              </Typography>
            </Box>

            {selectedExchanges.map((exchangeId) => {
              const exchange = exchanges.find((item: Exchange) => item.id === exchangeId);
              const submitted = submittedExchanges.find((item) => item.exchange_id === exchangeId);

              return (
                <Card key={exchangeId}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <ExchangeLogo
                        src={exchange?.logo_url}
                        name={exchange?.name}
                        id={exchangeId}
                        size={30}
                      />
                      <Typography variant="subtitle1">
                        {exchange?.name || exchangeId}
                      </Typography>
                    </Stack>

                    {!submitted ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          size="small"
                          placeholder={t('onboarding.enterUid')}
                          value={exchangeUIDs[exchangeId] || ''}
                          onChange={(event) =>
                            setExchangeUIDs((current) => ({ ...current, [exchangeId]: event.target.value }))
                          }
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          onClick={() => handleSubmitUID(exchangeId)}
                          disabled={!exchangeUIDs[exchangeId] || submitUIDMutation.isPending}
                        >
                          {t('common.submit')}
                        </Button>
                      </Stack>
                    ) : (
                      <Stack spacing={1}>
                        <Alert severity="success">{t('onboarding.uid', { uid: submitted.exchange_uid })}</Alert>

                        {!submitted.screenshot_url ? (
                          <Box>
                            <input
                              type="file"
                              accept="image/*"
                              id={`upload-${exchangeId}`}
                              style={{ display: 'none' }}
                              onChange={(event) => handleFileUpload(submitted.id, event)}
                            />
                            <label htmlFor={`upload-${exchangeId}`}>
                              <Button
                                variant="outlined"
                                component="span"
                                startIcon={
                                  uploadingFor === submitted.id ? <CircularProgress size={20} /> : <UploadIcon />
                                }
                                disabled={uploadingFor === submitted.id}
                              >
                                {t('onboarding.uploadScreenshot')}
                              </Button>
                            </label>
                          </Box>
                        ) : (
                          <Alert severity="success">{t('onboarding.screenshotUploaded')}</Alert>
                        )}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        );

      default:
        return null;
    }
  };

  if (mockCompleted) {
    return (
      <OnboardingScrollFrame>
        <PageShell
          eyebrow={t('onboarding.mockEyebrow')}
          title={t('onboarding.title')}
          action={<LanguageSelector />}
        >
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Alert severity="success">
                  {t('onboarding.mockComplete')}
                </Alert>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="contained" onClick={handleRestartMock}>
                    {t('onboarding.restartMock')}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/')}>
                    {t('onboarding.openApp')}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </PageShell>
      </OnboardingScrollFrame>
    );
  }

  return (
    <OnboardingScrollFrame>
      <PageShell
        eyebrow={mockMode ? t('onboarding.mockEyebrow') : t('onboarding.eyebrow')}
        title={t('onboarding.title')}
        action={<LanguageSelector />}
      >
        {mockMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('onboarding.mockNotice')}
          </Alert>
        )}
        <Box
          sx={{
            p: 1.5,
            mb: 2.5,
            borderRadius: 2,
            border: '1px solid rgba(226, 255, 238, 0.12)',
            bgcolor: 'rgba(244, 255, 249, 0.045)',
          }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ my: 2.5 }}>{renderStepContent()}</Box>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((current) => current - 1)}>
            {t('common.back')}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed() || completeMutation.isPending}
          >
            {activeStep === steps.length - 1 ? t('common.complete') : t('common.next')}
          </Button>
        </Stack>
      </PageShell>
    </OnboardingScrollFrame>
  );
}
